import { useCallback, useState } from 'react';

import { FLY_ANIMATION_MS } from './constants';

export type FlyGhostState = {
  label: string;
  top: number;
  left: number;
  width: number;
  height: number;
  opacity: number;
  transition: string;
};

const prefersReducedMotion = (): boolean => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

/**
 * FLIP-style flyover from a source element to a DOM target (by selector).
 */
export const useFlyToTargetAnimation = () => {
  const [ghost, setGhost] = useState<FlyGhostState | null>(null);

  const flyToTarget = useCallback(
    (
      sourceRect: DOMRect | HTMLElement,
      targetSelector: string,
      label: string,
      onComplete: () => void,
    ) => {
      const targetEl = document.querySelector(
        targetSelector,
      ) as HTMLElement | null;

      if (prefersReducedMotion() || !targetEl) {
        onComplete();
        return;
      }

      const from =
        sourceRect instanceof HTMLElement
          ? sourceRect.getBoundingClientRect()
          : sourceRect;
      const to = targetEl.getBoundingClientRect();
      const transition = `top ${FLY_ANIMATION_MS}ms ease-in-out, left ${FLY_ANIMATION_MS}ms ease-in-out, width ${FLY_ANIMATION_MS}ms ease-in-out, height ${FLY_ANIMATION_MS}ms ease-in-out, opacity ${FLY_ANIMATION_MS}ms ease-in-out`;

      setGhost({
        label,
        top: from.top,
        left: from.left,
        width: from.width,
        height: from.height,
        opacity: 1,
        transition: 'none',
      });

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setGhost({
            label,
            top: to.top,
            left: to.left,
            width: to.width,
            height: to.height,
            opacity: 0,
            transition,
          });
        });
      });

      window.setTimeout(() => {
        setGhost(null);
        onComplete();
      }, FLY_ANIMATION_MS + 60);
    },
    [],
  );

  return { ghost, flyToTarget };
};
